// SOW Component Type Definitions
// Shared types for configuration, data, component props, and utilities.

// ============================================
// PRIMITIVE TYPES
// ============================================

// Property type - specific property classification
export enum PropertyType {
  SFR = 'SFR',
  Condo = 'Condo',
  Townhouse = 'Townhouse',
  MultiUnit = 'multi-unit',
}

export interface LoanDataPropertyTypeLike {
  key?: string;
  display?: string;
  code?: string;
}

// Human-readable label for property type.
export function getPropertyTypeDisplayText(propertyType: PropertyType): string {
  switch (propertyType) {
    case PropertyType.SFR:
      return 'Single Family Residence';
    case PropertyType.Condo:
      return 'Condo';
    case PropertyType.Townhouse:
      return 'Townhouse';
    case PropertyType.MultiUnit:
      return 'Multi-Unit (2-30)';
    default:
      return propertyType;
  }
}

// Template type - determines which SOW template to use
export type TemplateType = 'single-family' | 'multi-family';

// Map property type to template type
// SFR, Condo, Townhouse use single-family template
// All others (multi-unit 2-30 units) use multi-family template
export function getTemplateType(propertyType: PropertyType): TemplateType {
  switch (propertyType) {
    case PropertyType.SFR:
    case PropertyType.Condo:
    case PropertyType.Townhouse:
      return 'single-family';
    case PropertyType.MultiUnit:
    default:
      return 'multi-family';
  }
}

// Check if a property type uses the single-family template
export function isSingleFamilyTemplate(propertyType: PropertyType): boolean {
  return getTemplateType(propertyType) === 'single-family';
}

// Map LoanData PropertyType to SOW PropertyType
// LoanData uses PROPERTY_TYPES from PickList.ts, SOW uses its own PropertyType enum
export function mapLoanDataPropertyTypeToSOW(loanDataPropertyType: LoanDataPropertyTypeLike | undefined): PropertyType {
  if (!loanDataPropertyType?.key) {
    return PropertyType.SFR; // Default fallback
  }

  const key = loanDataPropertyType.key;

  // Map based on key values from PROPERTY_TYPES
  switch (key) {
    case 'SFR':
      return PropertyType.SFR;
    case 'CONDO':
      return PropertyType.Condo;
    case 'TWN':
      return PropertyType.Townhouse;
    case 'MF2-4':
    case 'MF5-30':
    case 'MXD':
    case 'COMM':
      return PropertyType.MultiUnit;
    default:
      return PropertyType.SFR; // Default fallback
  }
}

// Category scope - determines layout and data structure
export type CategoryScope = 'building-wide' | 'per-unit';

// Contingency calculation mode
export type ContingencyMode = 'auto' | 'manual';

// PDF export format type
export type PDFExportType = 'report' | 'flat';

// Currency value (always stored as whole dollars to avoid floating point issues)
export type CurrencyValue = number;

// ============================================
// CONFIGURATION TYPES
// ============================================

// Configuration for a single input field
export interface FieldConfig {
  // Unique identifier for the field
  readonly id: string;
  // Display label for the field
  readonly label: string;
}

// Configuration for a category (section) of fields
export interface CategoryConfig {
  // Unique identifier for the category
  readonly id: string;
  // Display name for the category
  readonly name: string;
  // Whether this category is building-wide or per-unit
  readonly scope: CategoryScope;
  // Whether this category has a description field
  readonly hasDescription: boolean;
  // Help text / instructions for the category
  readonly instructions: string;
  // Fields within this category
  readonly fields: readonly FieldConfig[];
}

// Global configuration for the SOW component
export interface SOWConfig {
  // Maximum number of units allowed
  readonly maxUnits: number;
  // Minimum number of units for multi-unit properties
  readonly minUnits: number;
  // Maximum total amount allowed (5 million dollar limit)
  readonly maxTotalAmount: CurrencyValue;
  // Default property type when no initial data is provided
  readonly defaultPropertyType: PropertyType;
  // Contingency percentage (as decimal, e.g., 0.05 for 5%)
  readonly contingencyPercentage: number;
  // Minimum contingency amount
  readonly contingencyMinimum: CurrencyValue;
  // All category configurations
  readonly categories: readonly CategoryConfig[];
}

// ============================================
// DATA TYPES
// ============================================

// Field values for a single unit or building-wide
// Maps field ID to currency value
export type FieldValues = Readonly<Record<string, CurrencyValue>>;

// Data for a single category
export interface CategoryData {
  // Building-wide field values (used for all scopes)
  readonly buildingWide: FieldValues;
  // Per-unit field values (only for per-unit categories)
  readonly units: readonly FieldValues[];
  // Category description
  readonly description: string;
}

// Contingency data with calculation metadata
export interface ContingencyData {
  // Current contingency value
  readonly value: CurrencyValue;
  // Whether value is auto-calculated or manually set
  readonly mode: ContingencyMode;
  // The auto-calculated value (for reference when in manual mode)
  readonly calculatedValue: CurrencyValue;
}

// Complete SOW form data
export interface SOWData {
  // Property address
  readonly propertyAddress: string;
  // Property type
  readonly propertyType: PropertyType;
  // Number of units (1 for SFR)
  readonly unitCount: number;
  // Category data keyed by category ID
  readonly categories: Readonly<Record<string, CategoryData>>;
  // Contingency information
  readonly contingency: ContingencyData;
  // Calculated subtotal (sum of all fields)
  readonly subtotal: CurrencyValue;
  // Calculated total (subtotal + contingency)
  readonly total: CurrencyValue;
}

// Expanded/collapsed state for category sections
export type ExpandedSections = Readonly<Record<string, boolean>>;

// ============================================
// COMPONENT PROP TYPES
// ============================================

// Props for the main ScopeOfWork component
export interface SOWExportContext {
  loanId?: string;
  propertyState?: string;
}

export interface ScopeOfWorkProps {
  // Initial data to populate the form
  initialData?: Partial<SOWData>;
  // Callback when user saves the form
  onSave: (data: SOWData) => Promise<boolean> | boolean | void;
  // Whether a save operation is currently in progress
  saveLoading?: boolean;
  // Current save status from the shared application save pipeline
  saveStatus?: string | null;
  // Whether save actions are currently allowed
  canSave?: boolean;
  // Optional callback when user clicks Next
  onNextSection?: () => void;
  // Optional callback when user clicks Back
  onPreviousSection?: () => void;
  // Optional flag to disable Back button
  previousDisabled?: boolean;
  // Optional change notification for dirty tracking
  onChange?: (data: SOWData, isDirty: boolean) => void;
  // Whether the form is read-only
  readOnly?: boolean;
  // Optional CSS class name
  className?: string;
  // Whether to use full-width layout for foundation tables
  fullWidth?: boolean;
  // Optional export naming context (loan id + state)
  exportContext?: SOWExportContext;
  // Canonical application property type used to keep SOW behavior in sync
  syncedPropertyType?: PropertyType;
  // Canonical application display text for header rendering
  propertyTypeDisplayText?: string;
  // The approved rehab holdback amount
  rehabHoldback: number;
}

// Props for the header component
export interface SOWHeaderProps {
  // Current property type
  propertyType: PropertyType;
  // Optional canonical property type label from the application field source
  propertyTypeDisplayText?: string;
  // Current unit count
  unitCount: number;
  // Whether a unit can be added
  canAddUnit: boolean;
  // Whether a unit can be removed
  canRemoveUnit: boolean;
  // Callback to add a unit
  onAddUnit: () => void;
  // Callback to remove a unit
  onRemoveUnit: () => void;
  // Callback when save and next is clicked
  onSaveAndNext?: () => void;
  // Whether save operation is currently in progress
  saveLoading?: boolean;
  // Current save status from the shared application save pipeline
  saveStatus?: string | null;
  // Whether save actions are currently allowed
  canSave?: boolean;
  // Callback when summary PDF export is requested
  onExportSummaryPDF: () => void;
  // Callback when summary CSV export is requested
  onExportSummaryCSV: () => void;
  // Callback when summary ZIP export is requested
  onExportSummaryZIP: () => void;
  // Callback when flat PDF export is requested
  onExportFlatPDF: () => void;
  // Callback when flat CSV export is requested
  onExportFlatCSV: () => void;
  // Callback when flat ZIP export is requested
  onExportFlatZIP: () => void;
  // Callback to expand sections with data
  onExpandFilled: () => void;
  // Callback to collapse all sections
  onCollapseAll: () => void;
  // Whether there are filled sections that are currently collapsed
  canExpandFilled?: boolean;
  // Whether there are any filled sections at all
  hasAnyValues?: boolean;
  // Whether the form is read-only
  readOnly?: boolean;
}

// Props for a category section component
export interface CategorySectionProps {
  category: CategoryConfig;
  data: CategoryData;
  propertyType: PropertyType;
  unitCount: number;
  subtotal: CurrencyValue;
  isExpanded: boolean;
  onToggle: () => void;
  onFieldChange: (fieldId: string, value: CurrencyValue, unitIndex?: number) => void;
  onDescriptionChange: (description: string) => void;
  readOnly?: boolean;
}

// Props for the totals footer component
export interface TotalsFooterProps {
  subtotal: CurrencyValue;
  contingency: ContingencyData;
  onContingencyChange: (value: CurrencyValue) => void;
  onContingencyModeChange: (mode: ContingencyMode) => void;
  readOnly?: boolean;
  rehabHoldback: number;
}

// Props for currency input component
export interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
}

// ============================================
// UTILITY TYPES
// ============================================

// PDF export options
export interface PDFExportOptions {
  // Export format type
  type: PDFExportType;
  // Whether to include sections with no filled fields
  includeEmptySections: boolean;
  // Text to show for empty sections (if includeEmptySections is true)
  emptyText: string | null;
}

// ============================================
// STATE TYPES
// ============================================

// State for the SOW reducer (extends SOWData with dirty flag)
export interface SOWState extends SOWData {
  isDirty: boolean;
}

// Actions for the SOW reducer
export type SOWAction =
  | { type: 'SET_PROPERTY_ADDRESS'; payload: string }
  | { type: 'SET_PROPERTY_TYPE'; payload: PropertyType }
  | { type: 'SET_UNIT_COUNT'; payload: number }
  | { type: 'SET_FIELD_VALUE'; payload: { categoryId: string; fieldId: string; value: CurrencyValue; unitIndex?: number } }
  | { type: 'SET_DESCRIPTION'; payload: { categoryId: string; description: string } }
  | { type: 'SET_CONTINGENCY_VALUE'; payload: CurrencyValue }
  | { type: 'SET_CONTINGENCY_MODE'; payload: ContingencyMode }
  | { type: 'RESET'; payload?: Partial<SOWData> };
